/**
 * Risk Calculation Rules
 * Calculates patient risk from available data
 *
 * Priority order:
 * 1. Callouts/flags from EHR (if present)
 * 2. Medication adherence risk (Medicare + specialty drugs + age)
 * 3. Days since last visit (if date available)
 * 4. Concerning status values
 * 5. Default to "Needs Attention" (medium)
 */

import dayjs from 'dayjs';
import { DEFAULT_SPECIALTY_DRUGS } from './drugWatchlist';

// Re-export so existing imports from riskRules continue to work
export { DEFAULT_SPECIALTY_DRUGS };

// High-risk flags from EHR Callouts
const HIGH_RISK_FLAGS = [
  'provenge candidate',
  'xofigo candidate',
  'scan due to rising psa',
  'rising psa',
  'overdue adt',
  'urgent',
  'critical',
  'high priority',
  'immediate attention',
];

// Medium-risk flags from EHR Callouts
const MEDIUM_RISK_FLAGS = [
  'overdue appt',
  'overdue appointment',
  'overdue psa',
  'overdue tt',
  'overdue routine scan',
  'no next appointment',
  'not navigated',
  'missed appointment',
  'follow up needed',
  'follow-up needed',
  'needs follow up',
  'callback needed',
];

// Low-risk indicators
const LOW_RISK_FLAGS = [
  'routine',
  'stable',
  'wellness check',
  'annual',
];

// Concerning status values from EHR exports
const HIGH_RISK_STATUSES = [
  'urgent',
  'critical',
  'priority',
  'stat',
  'emergency',
];

const MEDIUM_RISK_STATUSES = [
  'callback',
  'callback needed',
  'needs callback',
  'no show',
  'no-show',
  'noshow',
  'missed',
  'cancelled',
  'canceled',
  'overdue',
  'past due',
  'awaiting results',
  'pending results',
  'pending',
  'follow up',
  'follow-up',
  'followup',
  'rescheduled',
];

/**
 * Parse callouts string into individual flags
 */
function parseCallouts(callouts) {
  if (!callouts || typeof callouts !== 'string') return [];

  return callouts
    .split(/[,;]/)
    .map(flag => flag.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Calculate risk from Callouts column
 * @param {string} callouts - Comma-separated flags from EHR
 * @returns {Object|null} - { risk, reason, source } or null if no callouts
 */
export function calculateRiskFromCallouts(callouts) {
  const flags = parseCallouts(callouts);

  if (flags.length === 0) return null;

  // Check for high-risk flags
  const highRiskMatches = flags.filter(flag =>
    HIGH_RISK_FLAGS.some(hrf => flag.includes(hrf))
  );

  if (highRiskMatches.length > 0) {
    return {
      risk: 'high',
      reason: `EHR flags: ${highRiskMatches.slice(0, 2).join(', ')}`,
      source: 'calculated',
      triggeredRules: ['callouts_high_risk'],
    };
  }

  // Check for medium-risk flags
  const mediumRiskMatches = flags.filter(flag =>
    MEDIUM_RISK_FLAGS.some(mrf => flag.includes(mrf))
  );

  // Multiple medium flags = high risk
  if (mediumRiskMatches.length >= 3) {
    return {
      risk: 'high',
      reason: `Multiple EHR flags (${mediumRiskMatches.length}): ${mediumRiskMatches.slice(0, 2).join(', ')}`,
      source: 'calculated',
      triggeredRules: ['callouts_multiple_flags'],
    };
  }

  if (mediumRiskMatches.length > 0) {
    return {
      risk: 'medium',
      reason: `EHR flags: ${mediumRiskMatches.slice(0, 2).join(', ')}`,
      source: 'calculated',
      triggeredRules: ['callouts_medium_risk'],
    };
  }

  // Check for explicit low-risk flags
  const lowRiskMatches = flags.filter(flag =>
    LOW_RISK_FLAGS.some(lrf => flag.includes(lrf))
  );

  if (lowRiskMatches.length > 0) {
    return {
      risk: 'low',
      reason: `EHR status: ${lowRiskMatches[0]}`,
      source: 'calculated',
      triggeredRules: ['callouts_low_risk'],
    };
  }

  // Has flags but none matched our rules - treat as medium
  return {
    risk: 'medium',
    reason: `Has EHR flags: ${flags.slice(0, 2).join(', ')}`,
    source: 'calculated',
    triggeredRules: ['callouts_unmatched'],
  };
}

/**
 * Calculate risk from patient status field
 * @param {string} status - Patient status from EHR
 * @returns {Object|null} - { risk, reason, source } or null if no concerning status
 */
export function calculateRiskFromStatus(status) {
  if (!status || typeof status !== 'string') return null;

  const normalizedStatus = status.toLowerCase().trim();

  // Check for high-risk statuses
  for (const keyword of HIGH_RISK_STATUSES) {
    if (normalizedStatus.includes(keyword)) {
      return {
        risk: 'high',
        reason: `EHR flagged: ${status}`,
        source: 'calculated',
        triggeredRules: ['flag_urgent_status'],
      };
    }
  }

  // Check for medium-risk statuses
  for (const keyword of MEDIUM_RISK_STATUSES) {
    if (normalizedStatus.includes(keyword)) {
      return {
        risk: 'medium',
        reason: `EHR flagged: ${status}`,
        source: 'calculated',
        triggeredRules: ['flag_concerning_status'],
      };
    }
  }

  return null;
}

/**
 * Calculate age from date of birth
 */
function calculateAge(dob) {
  if (!dob) return null;
  const birthDate = dayjs(dob);
  if (!birthDate.isValid()) return null;
  return dayjs().diff(birthDate, 'year');
}

/**
 * Calculate risk from medication adherence factors
 * Checks 3 criteria: Medicare insurance, specialty drug match, age >= threshold
 * All 3 → high, 2 of 3 → medium, 0-1 → null (no flag)
 *
 * @param {Object} patient - Patient record with insurance, medications, dob
 * @param {Object} config - { age_threshold, drug_list }
 * @returns {Object|null} - { risk, reason, source, triggeredRules } or null
 */
export function calculateRiskFromMedAdherence(patient, config = {}) {
  const { age_threshold = 65, drug_list = DEFAULT_SPECIALTY_DRUGS } = config;

  let matches = 0;
  const reasons = [];

  // Criterion 1: Medicare insurance
  if (patient.insurance && patient.insurance.toLowerCase().includes('medicare')) {
    matches++;
    reasons.push('Medicare');
  }

  // Criterion 2: Specialty drug match
  if (patient.medications) {
    const medsLower = patient.medications.toLowerCase();
    const matchedDrug = drug_list.find(drug => medsLower.includes(drug.toLowerCase()));
    if (matchedDrug) {
      matches++;
      reasons.push(matchedDrug);
    }
  }

  // Criterion 3: Age >= threshold
  const age = calculateAge(patient.dob);
  if (age !== null && age >= age_threshold) {
    matches++;
    reasons.push(`age ${age}`);
  }

  // Build a human-readable reason with age first if available
  const ageReason = reasons.find(r => r.startsWith('age '));
  const drugReason = reasons.find(r => !r.startsWith('age ') && r !== 'Medicare');
  const hasMedicare = reasons.includes('Medicare');
  const readableParts = [];
  if (ageReason) readableParts.push(ageReason.replace('age ', '') + 'yo');
  if (drugReason) readableParts.push(`on ${drugReason}`);
  if (hasMedicare) readableParts.push('Medicare');
  const readableReason = readableParts.join(', ');

  if (matches >= 3) {
    return {
      risk: 'high',
      reason: `High-risk for drop-off: ${readableReason} — may need refill support`,
      source: 'calculated',
      triggeredRules: ['med_adherence_risk'],
    };
  }

  if (matches === 2) {
    return {
      risk: 'medium',
      reason: `Potential drop-off risk: ${readableReason}`,
      source: 'calculated',
      triggeredRules: ['med_adherence_risk'],
    };
  }

  return null;
}

/**
 * Calculate risk from days since last visit
 * @param {string} lastVisit - ISO date string
 * @param {Object} config - Rule config { high_days, medium_days }
 * @returns {Object|null} - { risk, reason, source } or null if no date
 */
export function calculateRiskFromLastVisit(lastVisit, config = {}) {
  if (!lastVisit) return null;

  const { high_days = 90, medium_days = 30 } = config;

  const lastDate = dayjs(lastVisit);
  if (!lastDate.isValid()) return null;

  const daysSince = dayjs().diff(lastDate, 'day');

  if (daysSince >= high_days) {
    return {
      risk: 'high',
      reason: `Last visit was ${daysSince} days ago — no follow-up scheduled`,
      source: 'calculated',
      triggeredRules: ['overdue_appointment'],
    };
  }

  if (daysSince >= medium_days) {
    return {
      risk: 'medium',
      reason: `Last visit was ${daysSince} days ago — may need follow-up`,
      source: 'calculated',
      triggeredRules: ['overdue_appointment'],
    };
  }

  return {
    risk: 'low',
    reason: `Last visit ${daysSince} days ago`,
    source: 'calculated',
    triggeredRules: [],
  };
}

/**
 * Calculate risk for a patient using all available data
 * Priority: Callouts → Med adherence → Days overdue → Status → Default
 *
 * @param {Object} patient - Patient record with callouts, last_touch, etc.
 * @param {Object} ruleConfig - Clinic-specific rule configurations
 * @returns {Object} - { risk, reason, source, triggeredRules }
 */
export function calculatePatientRisk(patient, ruleConfig = {}) {
  // 1. Try Callouts first (EHR already did the work)
  if (patient.callouts) {
    const calloutsRisk = calculateRiskFromCallouts(patient.callouts);
    if (calloutsRisk) return calloutsRisk;
  }

  // 2. Try medication adherence risk
  if (ruleConfig.med_adherence_risk !== false) {
    const medConfig = ruleConfig.med_adherence_risk || {};
    const medRisk = calculateRiskFromMedAdherence(patient, medConfig);
    if (medRisk) return medRisk;
  }

  // 3. Try days since last visit
  if (patient.last_touch) {
    const overdueConfig = ruleConfig.overdue_appointment || { high_days: 90, medium_days: 30 };
    const visitRisk = calculateRiskFromLastVisit(patient.last_touch, overdueConfig);
    if (visitRisk) return visitRisk;
  }

  // 4. Check for concerning status values
  if (patient.status && ruleConfig.flag_concerning_status !== false) {
    const statusRisk = calculateRiskFromStatus(patient.status);
    if (statusRisk) return statusRisk;
  }

  // 5. No data available - default to medium (Needs Attention)
  return {
    risk: 'medium',
    reason: 'Insufficient data for risk calculation',
    source: 'default',
    triggeredRules: [],
  };
}

/**
 * Batch calculate risk for multiple patients
 * @param {Object[]} patients - Array of patient records
 * @param {Object} ruleConfig - Clinic-specific rule configurations
 * @returns {Object[]} - Patients with risk, risk_source, ai_reason, triggered_rules set
 */
export function calculateBatchRisk(patients, ruleConfig = {}) {
  return patients.map(patient => {
    const riskResult = calculatePatientRisk(patient, ruleConfig);

    return {
      ...patient,
      risk: riskResult.risk,
      risk_source: riskResult.source,
      ai_reason: riskResult.reason,
      triggered_rules: riskResult.triggeredRules,
    };
  });
}

/**
 * Get risk stats from calculated patients
 */
export function getRiskStats(patients) {
  const stats = {
    high: 0,
    medium: 0,
    low: 0,
    defaulted: 0,
  };

  for (const patient of patients) {
    if (patient.risk === 'high') stats.high++;
    else if (patient.risk === 'medium') stats.medium++;
    else if (patient.risk === 'low') stats.low++;

    if (patient.risk_source === 'default') stats.defaulted++;
  }

  return stats;
}
